/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
// import { name, random } from 'faker';
import gql from 'graphql-tag';
import { FetchPolicy } from 'apollo-client';
import { TextField, CircularProgress } from '@material-ui/core';
import BusinessOutlinedIcon from '@material-ui/icons/BusinessOutlined';
import { IAutoCompleteItem } from '../../typings/autoComplete';
import { showNotification } from '../../App';
import { client } from '../..';
import { InfiniteAutocomplete } from '../../components/InfiniteAutocomplete';
import { RenderInputParams } from '@material-ui/lab';
import { useDebouncedCallback } from 'use-debounce/lib';
import clsx from 'clsx';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
        minWidth: '200px',
    },
    textField: {
        width: '100%',
    },
    // The Autocomplete's own end adornment is pinned to the right of the input (position: absolute), so the extra button is laid out with it
    // in one flex row instead of competing for that spot (same as IndividualSelector).
    endAdornmentRow: {
        position: 'absolute',
        right: 0,
        top: 'calc(50% - 14px)',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
    },
    iconSelected: {
        width: 17,
        height: 17,
        marginRight: 5,
        marginLeft: -2,
      },
      color: {
        width: 14,
        height: 14,
        flexShrink: 0,
        borderRadius: 3,
        marginRight: 8,
        marginTop: 2,
      },
      text: {
        flexGrow: 1,
      },
      close: {
        opacity: 0.6,
        width: 18,
        height: 18,
      },
      renderWrapper: {
          padding: '15px 0'
      },
      customRenderWrapper: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        minWidth: 0,
        fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
        '& .avatar': {
            flex: '0 0 auto',
            width: 34,
            height: 34,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(94, 53, 177, 0.12)',
            color: '#5e35b1',
        },
        '& .primary': {
            flex: 1,
            minWidth: 0,
            fontSize: '0.9375rem',
            fontWeight: 600,
            lineHeight: 1.4,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
        // an organisation typed in by hand, not one from the data source
        '& .entered': {
            flex: '0 0 auto',
            padding: '1px 8px',
            borderRadius: 999,
            backgroundColor: '#f2f4fa',
            color: '#667085',
            fontSize: '0.6875rem',
            fontWeight: 600,
            whiteSpace: 'nowrap',
        },
      },
  })
);

export type OrganisationIAutoCompleteItem = IAutoCompleteItem & NameSummary;

interface OrganisationSelectorProps {
    isSourceTypeClientOnly?: boolean; // this is to determine whether to include records with source type as client only, which is applicable for Affinity tenant
    isStaffOnly?: boolean;
    multiple?: boolean;
    label?: string;
    name: string;
    // tslint:disable-next-line: no-any
    value?: IAutoCompleteItem | any; // value only available in single select
    onSelection?: (value: OrganisationIAutoCompleteItem | OrganisationIAutoCompleteItem[] | IAutoCompleteItem | IAutoCompleteItem[], name: string) => void;
    id?: string;
    required?: boolean;
    helperText?: string;
    error?: boolean;
    className?: string;
    disablePortal?: boolean;
    disabled?: boolean;
    variant?: 'standard' | 'outlined' | 'filled';
    size?: 'small' | 'medium';
    ChipProps?: object;
    endAdornmentExtra?: React.ReactNode;
}

interface OrganisationSelectorState {
    hasNextPage: boolean;
    isNextPageLoading: boolean;
    offset: number;
    items: OrganisationIAutoCompleteItem[];
    filter: string;
}

export const OrganisationSelector: React.FC<OrganisationSelectorProps> = props => {

    const classes = useStyles();
    const loadBlockLimit = 20;

    const [delayedonInputChange] = useDebouncedCallback(
        // function
        (event: React.ChangeEvent<{}>, value: string, reason: 'input' | 'reset' | 'clear') => {
            fetchData(value, 0); 
        },
        // delay in ms
        500
    );
    
    const [state, setState] = useState<OrganisationSelectorState>({
        hasNextPage: true,
        isNextPageLoading: false,
        offset: 0,
        items: [],
        filter: ''
    });

    useEffect(() => {
        fetchData('', 0);
    // tslint:disable-next-line: align
    }, []);

    const fetchData = (filter: string, offset: number) => {

        setState((prevState) => {
            return {
                ...prevState,
                isNextPageLoading: true,
                offset: offset,
                filter: filter
            };
        });

        const partyQueryParams: PartyQueryParams = {
            filter: filter.length > 0 ? filter : null,
            first: loadBlockLimit,
            offset: offset,
            includeOrganisation: true,
            includeIndividual: null,
            includeStaff: null,
            isSourceTypeClientOnly: props.isSourceTypeClientOnly
        };

        retrievePartyData(
            partyQueryParams,
            true, // this.state.isRefresh, -- Since we are updating and creating, we do not fetch cached data
            // tslint:disable-next-line: no-console
            (data) => onRetrieveUserList(data, filter),
            // tslint:disable-next-line:no-any
            function (reason: any): void {
                showNotification(null, reason, 'error');

                setState((prevState) => {
                    return {
                        ...prevState,
                        isNextPageLoading: false,
                    };
                });
            }
        );
    };

    const onRetrieveUserList = (data: PartyData, filter: string) => {

        const nameSummaryData = data.party.nameSummaries.nameSummary;
        const recordCount = data.party.nameSummaries.recordCount;

        // tslint:disable-next-line: no-any
        let itemsList: any = [...state.items];

        if (state.items.length === 0) {
            itemsList = nameSummaryData.map( (source: NameSummary) => ({
                ...source,
                value: source.guidID.toString(), 
                label: source.name 
            }));
        } else {
            for (const nameSummary of nameSummaryData) {
                // tslint:disable-next-line: no-any
                const isExists = state.items.filter((item: OrganisationIAutoCompleteItem) => 
                    item.value.toString().toLowerCase().includes(nameSummary.guidID.toString().toLowerCase())).length;

                if (isExists === 0) {
                    itemsList.push({
                        ...nameSummary,
                        label: nameSummary.name,
                        value: nameSummary.guidID.toString()
                    });
                }
            }
        }

        setState((prevState) => {
            return {
                ...prevState,
                filter: filter,
                isNextPageLoading: false,
                items: itemsList,
                offset: state.offset + loadBlockLimit,
                hasNextPage: nameSummaryData.length !== 0 && (state.offset + loadBlockLimit) < recordCount,
            };
        });
    };

    // tslint:disable-next-line: no-any
    const loadNextPage = (...args: any) => {
        if (state.hasNextPage && !state.isNextPageLoading) {
            fetchData(state.filter, state.offset);
        }
    };

    const getInputProps = (params: RenderInputParams, isLoading?: boolean) => {
        if (props.endAdornmentExtra) {
            const defaultEndAdornment = params.InputProps.endAdornment;
            const autocompleteEndAdornment = defaultEndAdornment && React.isValidElement<{ style?: React.CSSProperties }>(defaultEndAdornment)
                ? React.cloneElement(defaultEndAdornment, {
                    style: { ...defaultEndAdornment.props.style, position: 'static', top: 'auto', right: 'auto' },
                })
                : defaultEndAdornment;
            return ({
                ...params.InputProps,
                endAdornment: (
                    <div className={classes.endAdornmentRow}>
                      {isLoading ? <CircularProgress color="primary" size={20} /> : null}
                      {autocompleteEndAdornment}
                      {props.endAdornmentExtra}
                    </div>
                  ),
            });
        }
        return  ({
            ...params.InputProps,
            endAdornment: (
                <React.Fragment>
                  {isLoading ? <CircularProgress color="primary" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),     
         });
    };

    return (
        <div className={clsx(classes.root, props.className)}>
            <InfiniteAutocomplete
                id={props.id}
                itemSize={52}
                loading={state.isNextPageLoading}
                disablePortal={props.disablePortal}
                name={props.name}
                isMultiple={props.multiple}
                value={props.value}
                hasNextPage={state.hasNextPage}
                isNextPageLoading={state.isNextPageLoading}
                items={state.items}
                loadNextPage={loadNextPage}
                // disableCloseOnSelect={true}
                className={classes.textField}
                disableListWrap={true}
                onSelection={props.onSelection}
                ChipProps={props.ChipProps}
                getOptionLabel={(option: OrganisationIAutoCompleteItem) => option.label}
                // Explicit bug report: missing getOptionSelected meant MUI's default
                // reference-equality "is value in options" check failed for every valid
                // selection too (a fresh object every render is never === the matching options
                // entry), on top of InfiniteAutocomplete's own now-fixed raw-value issue.
                getOptionSelected={(option: OrganisationIAutoCompleteItem, value: OrganisationIAutoCompleteItem) => option.value === value.value}
                onInputChange={delayedonInputChange}
                disabled={props.disabled}
                renderInput={params => (
                    <TextField
                        {...params}
                        variant={props.variant ?? 'standard'}
                        size={props.size}
                        label={props.label}
                        fullWidth={true}
                        margin="none"
                        required={props.required}
                        InputProps={getInputProps(params, state.isNextPageLoading)}
                        error={props.error}      
                        helperText={props.helperText}  
                    />
                )}
                renderOption={(option, { selected }) => {
                    return (
                        <div className={classes.customRenderWrapper}>
                            <div className="avatar">
                                <BusinessOutlinedIcon fontSize="small" />
                            </div>
                            <div className="primary" title={option.label}>
                                {option.label}
                            </div>
                            {!option.isFromDataSource ? (
                                <div className="entered">
                                    Entered
                                </div>
                            ) : null}
                        </div>
                    );
                }}
            />
        </div>
    );
};

export function retrievePartyData(
    query: PartyQueryParams,
    refreshData: boolean,
    onSuccess: (data: PartyData) => void,
    // tslint:disable-next-line:no-any
    onError: (reason: any) => void
): void {
    var fetchPolicy: FetchPolicy = refreshData === true ? 'network-only' : 'cache-first';
    client
        .query<PartyData, PartyQueryParams>({
            query: PartyQuery,
            variables: {
                filter: query.filter,
                first: query.first,
                offset: query.offset,
                includeIndividual: query.includeIndividual,
                includeOrganisation: query.includeOrganisation,
                includeStaff: query.includeStaff,
                isSourceTypeClientOnly: query.isSourceTypeClientOnly
            },
            fetchPolicy: fetchPolicy,
        })
        // tslint:disable-next-line:no-any
        .then((results: { data: any }) => {
            onSuccess(results.data);
        })
        // tslint:disable-next-line:no-any
        .catch((reason: any) => {
            onError(reason);
        });
}

export interface PartyQueryParams {
    offset: number | null;
    first: number | null;
    filter: string | null;
    includeIndividual: Boolean | null;
    includeOrganisation: Boolean | null;
    includeStaff: Boolean | null;
    isSourceTypeClientOnly?: Boolean | null;
}

export interface PartyData {
    loading?: boolean;
    networkStatus?: number;
    party: Party;
}

export interface Party {
    nameSummaries: NameSummaries;
}

export interface NameSummaries {
    recordCount: number;
    nameSummary: NameSummary[];
}

export interface NameSummary {
    id: number;
    guidID: string;
    nameId: number;
    name: string;
    address: string;
    isFromDataSource: boolean;
    nameSourceId: string;
    sourceType: string;
}

const PartyQuery = gql`
    query Party(
        $offset: Int,
        $first: Int,
        $filter: String,
        $includeIndividual: Boolean,
        $includeOrganisation: Boolean,
        $includeStaff: Boolean,
        $isSourceTypeClientOnly: Boolean
    ){
        party{
            nameSummaries(
                offset: $offset,
                first: $first,
                filter: $filter,
                includeIndividual: $includeIndividual,
                includeOrganisation: $includeOrganisation,
                includeStaff: $includeStaff,
                isSourceTypeClientOnly: $isSourceTypeClientOnly
            ){
                recordCount
                nameSummary{
                    id,
                    nameId
                    guidID,
                    name
                    address
                    isFromDataSource
                    nameSourceId
                    sourceType
                }
            }
        }
    }
`;